import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { HELP_ANCHORS, useWhatsNew, type HelpAnchor } from "@keydials/ui";
import type { FeedbackClarificationListResponse, WorkspaceMode } from "@keydials/api-client";
import { api, getAccessToken, useAuth } from "../../auth/AuthContext";
import { Icon, type IconName } from "../Icon";
import { Avatar } from "../primitives";
import { KdLogo } from "../KdMark";
import { personFrom } from "../../lib/ui";
import { HelpModal } from "./modals";
import { useFeedbackDock } from "./FeedbackDock";
import { HelpLauncher } from "../../help/HelpLauncher";
import { NotificationCentre } from "./NotificationCentre";
import { WhatsNewModal } from "./WhatsNewModal";
import { useWorkspaceMode } from "../../workspace/WorkspaceModeProvider";
import { useTheme } from "../../theme/ThemeProvider";
import { ClarificationsModal } from "./ClarificationsModal";
import { SyncCalendarsMenuItem } from "./SyncCalendarsMenuItem";
import { kioskNav } from "../../lib/kiosk";
import { useAuthoring } from "../../authoring/AuthoringProvider";
import { builderCopy } from "../../authoring/copy";
import { EMPTY_CLARIFICATIONS, loadClarifications } from "./clarifications";
import { onNotify, onReconnected, type RealtimeEvent } from "../../realtime/connection";

/**
 * The brand lockup in the header: the full "Key Dials" wordmark, or just the KD mark once the header is too
 * narrow to carry both it and the controls beside it.
 *
 * The wordmark is 166px wide. That is most of what a 320px screen has to spend, and before the Help button
 * was allowed onto a phone at all the header only fit because Help was missing from it — with Help and
 * Replies both present the row overflowed and pushed the avatar off the right edge. Something had to give,
 * and the words are the part that carries no function.
 *
 * Rendered as a pair swapped by `kd-hide-sm`/`kd-show-sm` rather than picked in JS from a measured width:
 * no resize listener, and nothing flashes the wrong lockup on first paint.
 */
function HeaderLogo() {
  return (
    <>
      <span className="kd-hide-sm" style={{ display: "flex" }}>
        <KdLogo height={36} />
      </span>
      <span className="kd-show-sm">
        <KdLogo height={36} wordmark={false} />
      </span>
    </>
  );
}

/**
 * The white-label lockup (KEY-731), drawn when /api/me carries a brand. Two arrangements, swapped by CSS at
 * breakpoints measured in a browser (theme.css, `.kd-brand-*`) rather than chosen in JS:
 *
 * - **Wide:** "Powered by" + the KD mark in the left slot, and the partner's logo absolutely centred over the
 *   row. Absolutely positioned so it costs the flex row nothing — the row already had ~0px to spare.
 * - **Narrower:** the partner's logo takes the left slot itself, with a small KD mark after it; the centred
 *   logo is hidden, because between ~700px and the wide breakpoint the controls reach past the middle.
 *
 * The logo is the partner's own landscape image; a brand with no image (no asset store) falls back to its name.
 */
function BrandLogo({ name, logoUrl, className }: { name: string; logoUrl: string | null; className: string }) {
  return logoUrl ? (
    <img className={className} src={logoUrl} alt={name} />
  ) : (
    <span className={`${className} kd-brand-name`}>{name}</span>
  );
}

function BrandedHeaderLogo({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  return (
    <>
      <span className="kd-brand-powered" aria-label="Powered by Key Dials">
        <span aria-hidden>Powered by</span>
        <KdLogo height={22} wordmark={false} />
      </span>
      <span className="kd-brand-inline">
        <BrandLogo name={name} logoUrl={logoUrl} className="kd-brand-inline-logo" />
        <span className="kd-brand-inline-mark" title="Powered by Key Dials">
          <KdLogo height={18} wordmark={false} />
        </span>
      </span>
    </>
  );
}

function HeaderBtn({
  icon,
  label,
  onClick,
  active,
  badge,
  helpAnchorId,
}: {
  icon: IconName;
  label?: string;
  onClick?: () => void;
  active?: boolean;
  badge?: number;
  /** Optional anchor id so a guided-help tour can spotlight this button (KEY-535). Inert otherwise. */
  helpAnchorId?: HelpAnchor;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label ?? icon}
      data-help-anchor={helpAnchorId}
      // The box lives in theme.css because it is the part that changes with the viewport: the label below
      // is kd-header-label, so below 700px a labelled button is an icon on its own and should be the same
      // square as one that never had a label — it used to keep the 13px of padding the missing text needed.
      className={label ? "kd-header-btn kd-header-btn-labelled" : "kd-header-btn"}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height: 38,
        borderRadius: "var(--radius-sm)",
        border: `var(--border-w) solid ${active ? "var(--accent-border)" : "var(--header-btn-border)"}`,
        background: active ? "var(--accent-soft)" : "var(--header-btn-bg)",
        color: active ? "var(--accent-text)" : "var(--header-fg)",
        font: "inherit",
        fontSize: 13.5,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <Icon name={icon} size={17} />
      {label && <span className="kd-header-label">{label}</span>}
      {badge !== undefined && badge > 0 && (
        <span
          style={{
            position: "absolute",
            top: -5,
            right: -5,
            minWidth: 18,
            height: 18,
            padding: "0 4px",
            borderRadius: 99,
            background: "var(--st-overdue)",
            color: "#fff",
            fontSize: 10.5,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/** The dial views offered in the avatar menu (KEY-543), in the order they were built. */
const WORKSPACE_VIEWS: { mode: WorkspaceMode; label: string; icon: IconName; hint: string }[] = [
  { mode: "Classic", label: "Classic", icon: "target", hint: "The dial as it has always been" },
  { mode: "Dynamic", label: "Dynamic", icon: "sparkles", hint: "The dial turns to what you select, and the card is lit from it" },
  { mode: "Dynamic3D", label: "3D", icon: "layers", hint: "Dynamic, with the dial and the card turned toward each other" },
];

const menuHeading: CSSProperties = {
  padding: "6px 10px 4px",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
};

const menuDivider: CSSProperties = {
  height: 1,
  background: "var(--border)",
  margin: "6px 0",
};

const menuItem: CSSProperties = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: 9,
  padding: "9px 10px",
  border: "none",
  background: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 500,
  color: "var(--text)",
};

/** Small inline unread-count pill, shared by the avatar's at-a-glance badge and the dropdown item's count —
 * same visual as `HeaderBtn`'s badge, minus the absolute positioning each caller applies differently. */
function CountBadge({ count, style }: { count: number; style?: CSSProperties }) {
  return (
    <span
      style={{
        minWidth: 18,
        height: 18,
        padding: "0 4px",
        borderRadius: 99,
        background: "var(--st-overdue)",
        color: "#fff",
        fontSize: 10.5,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {count}
    </span>
  );
}

interface WhatsNewState {
  hasReleases: boolean;
  count: number;
  open: () => void;
}

/** Headless "What's New" data owner + auto-opening dialog. Only mounted once the user is signed in and not
 * kiosk-locked (so the initial fetch has an access token and doesn't 403 from behind the lock screen), and
 * mounted independent of the profile dropdown's open state — its trigger renders inside the dropdown, but the
 * hook itself must run regardless, or the initial fetch and auto-open-on-load behaviour would only kick in
 * once a user happens to open the dropdown, which never happens automatically. Reports its state up via
 * `onState` so the dropdown trigger and the avatar's at-a-glance badge (rendered elsewhere in the header) can
 * both use it. Shared open/close/dismiss behaviour lives in the useWhatsNew hook.
 */
function WhatsNewLauncher({ onState }: { onState: (state: WhatsNewState) => void }) {
  const { me } = useAuth();
  // Platform admins see every update and choose which are public; everyone else is only ever sent public ones.
  const canPublish = me?.capabilities.includes("tenant.cross") ?? false;
  const { releases, hasReleases, isOpen, open, close, setPublic } = useWhatsNew({
    listUnread: async () => {
      const token = getAccessToken();
      return token ? api.listUnreadReleases(token) : [];
    },
    dismiss: async (key) => {
      const token = getAccessToken();
      if (token) await api.dismissRelease(token, key);
    },
    setPublic: async (key, isPublic) => {
      const token = getAccessToken();
      if (token) await api.setReleasePublic(token, key, isPublic);
    },
  });

  useEffect(() => {
    onState({ hasReleases, count: releases.length, open });
  }, [hasReleases, releases.length, open, onState]);

  return isOpen ? (
    <WhatsNewModal
      releases={releases}
      onClose={close}
      loadAll={async () => {
        const token = getAccessToken();
        return token ? api.listAllReleases(token) : [];
      }}
      onSetPublic={canPublish ? setPublic : undefined}
    />
  ) : null;
}

/**
 * "Replies" header button (KEY-253) — the indicator beside Feedback that tells the user the team has asked a
 * follow-up question about something they reported. Deliberately separate from the notification bell, whose
 * items are all dial-scoped, and from the Feedback button, which submits rather than reads. Hidden entirely
 * until there is at least one conversation, so it costs nothing for users who've never been asked anything.
 */
function ClarificationsLauncher() {
  const [state, setState] = useState<FeedbackClarificationListResponse>(EMPTY_CLARIFICATIONS);
  const [isOpen, setIsOpen] = useState(false);

  const reload = useCallback(async () => {
    setState(await loadClarifications({ list: (t) => api.listMyFeedbackClarifications(t) }, getAccessToken()));
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The hub auto-joins the user's own group on connect, so this just reacts to the pushed event type.
  useEffect(() => {
    const offNotify = onNotify((e: RealtimeEvent) => {
      if (e.type === "feedback-reply") {
        void reload();
      }
    });
    const offReconnect = onReconnected(() => void reload());
    return () => {
      offNotify();
      offReconnect();
    };
  }, [reload]);

  if (state.items.length === 0) {
    return null;
  }

  return (
    <>
      <HeaderBtn
        icon="chat"
        label="Replies"
        onClick={() => setIsOpen(true)}
        badge={state.unreadCount > 0 ? state.unreadCount : undefined}
      />
      {isOpen && (
        <ClarificationsModal state={state} onState={setState} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { me, logout } = useAuth();
  const [modal, setModal] = useState<null | "help" | "videos">(null);
  const [menu, setMenu] = useState(false);
  const feedbackDock = useFeedbackDock();

  // A PIN-unlocked kiosk session (KEY-268) reaches one dial and nothing else, so the header drops every
  // control that would only 403: Feedback, Replies and What's New all read or write outside the dial.
  const { locked: kiosk, homePath } = kioskNav(me);
  const workspaceMode = useWorkspaceMode();
  const authoring = useAuthoring();
  const { theme, themes, setTheme } = useTheme();

  const person = me ? personFrom({ email: me.email, displayName: me.displayName }) : null;
  // KEY-731: the white-label brand for the organisation this session is scoped to. Null (or absent) — which
  // includes whenever Features:WhiteLabelHeader is off — draws the Key Dials header exactly as before.
  const brand = me?.brand ?? null;
  const lockup = brand ? <BrandedHeaderLogo name={brand.name} logoUrl={brand.logoUrl} /> : <HeaderLogo />;
  const [whatsNew, setWhatsNew] = useState<WhatsNewState>({ hasReleases: false, count: 0, open: () => {} });
  const handleWhatsNewState = useCallback((state: WhatsNewState) => setWhatsNew(state), []);

  return (
    <>
      <header
        style={{
          background: "var(--header-bg)",
          borderBottom: "var(--border-w) solid var(--header-border)",
          position: "sticky",
          top: 0,
          zIndex: 60,
        }}
      >
        <div
          className={brand ? "kd-header-row kd-header-row-branded" : "kd-header-row"}
          style={{
            position: "relative",
            maxWidth: 1320,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          {/* On a kiosk session the logo is not a way home — there is no home to go to — so it stops
              being a button rather than becoming a link that bounces straight back. */}
          {kiosk ? (
            <span style={{ display: "flex", alignItems: "center" }}>
              {lockup}
            </span>
          ) : (
            <button
              onClick={() => homePath && navigate(homePath)}
              aria-label={brand ? `${brand.name} home` : "Key Dials home"}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", minWidth: 0 }}
            >
              {lockup}
            </button>
          )}

          {/* KEY-731: the partner's logo, centred over the whole row on wide screens only (see BrandLogo). */}
          {brand && <BrandLogo name={brand.name} logoUrl={brand.logoUrl} className="kd-brand-centre" />}

          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <button
              onClick={() => setModal("help")}
              // This button is the ONLY thing anywhere in web-user that opens the help drawer, and it used
              // to carry kd-hide-sm itself — so below 560px it was `display: none` and all fifteen guides
              // were unreachable on a phone, the one place people are most likely to be stuck. It now
              // collapses the way HeaderBtn does instead: the label column below is dropped and the button
              // squares off to its icon (.kd-help-btn, theme.css) — at 700px, the header's own
              // label-shedding breakpoint, not kd-hide-sm's 560. The name has to be spelled out here
              // because Icon is aria-hidden: without it the collapsed button is nameless to a screen reader,
              // and it stops keeping the promise ANCHOR_LABELS makes that the word "Help" is on screen
              // wherever this anchor is (that map is matched against text, aria-label and title).
              title="Help"
              aria-label="Help"
              className="kd-help-btn"
              data-help-anchor={HELP_ANCHORS.helpButton}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                height: 38,
                borderRadius: "var(--radius-sm)",
                border: "var(--border-w) solid var(--header-btn-border)",
                background: "var(--header-btn-bg)",
                cursor: "pointer",
                font: "inherit",
                color: "var(--header-fg)",
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: "var(--accent-soft)",
                  color: "var(--accent-text)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="help" size={15} />
              </span>
              <span className="kd-header-label" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>Help</span>
                <span style={{ fontSize: 11, color: "var(--header-fg-muted)" }}>How do I…?</span>
              </span>
            </button>

            {!kiosk && <HeaderBtn icon="chat" label="Feedback" onClick={feedbackDock.open} helpAnchorId={HELP_ANCHORS.feedbackButton} />}
            {me && !kiosk && <ClarificationsLauncher />}
            <div style={{ width: 1, height: 24, background: "var(--header-divider)" }} className="kd-hide-sm" />
            <NotificationCentre />

            <div style={{ position: "relative" }}>
              <button
                onClick={() => setMenu((m) => !m)}
                aria-label="Account menu"
                data-help-anchor={HELP_ANCHORS.accountMenu}
                style={{ position: "relative", background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex" }}
              >
                {person && <Avatar person={person} size={38} ring={false} />}
                {me && !kiosk && whatsNew.hasReleases && (
                  <CountBadge count={whatsNew.count} style={{ position: "absolute", top: -5, right: -5 }} />
                )}
              </button>
              {menu && person && (
                <>
                  <div onClick={() => setMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "calc(100% + 8px)",
                      zIndex: 80,
                      width: 240,
                      background: "var(--surface)",
                      border: "var(--border-w) solid var(--border)",
                      borderRadius: "var(--radius)",
                      boxShadow: "var(--shadow-lg)",
                      padding: 8,
                    }}
                  >
                    <div style={{ padding: "8px 10px 10px", borderBottom: "var(--border-w) solid var(--border)", marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{person.name}</div>
                      <div style={{ fontSize: 12.5, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis" }}>{me?.email}</div>
                    </div>
                    {me && !kiosk && (
                      <button
                        onClick={() => { setMenu(false); whatsNew.open(); }}
                        className="kd-row-hover"
                        style={menuItem}
                      >
                        <Icon name="sparkles" size={16} style={{ color: "var(--text-faint)" }} />
                        What&apos;s New
                        {whatsNew.hasReleases && <CountBadge count={whatsNew.count} style={{ marginLeft: "auto" }} />}
                      </button>
                    )}
                    {/* KEY-560: the builder's way in for everyone, including the people who have one dial and are taken
                        straight to it, so never see My Dials. Offered only where the builder is. */}
                    {!kiosk && authoring.offered && (
                      <button onClick={() => { setMenu(false); navigate("/build/new"); }} className="kd-row-hover" style={menuItem}>
                        <Icon name="plus" size={16} style={{ color: "var(--text-faint)" }} />
                        {builderCopy.waysIn.buildButton}
                      </button>
                    )}
                    {/* Theme and the shared-device PIN used to sit loose in this menu beside a Settings
                        entry that did nothing at all. They are now ON that page, and this opens it.
                        Withheld from a kiosk session: the server refuses /api/me/profile and the PIN
                        routes to those tokens, so the page would be a wall of failed requests — and a
                        shared tablet is not where the account owner edits their own details. */}
                    {/* KEY-531. In the menu rather than on the Settings page because it is a way of LOOKING
                        at a dial, changed mid-task and changed back — the same reason the theme toggle used
                        to live here. Hidden entirely when the feature is off, so there is never a control
                        offering a mode this server does not have. Kiosk sessions are excluded on the same
                        grounds as Settings below: a shared tablet is not where display choices get made. */}
                    {/* KEY-543: a list, not a toggle. Two views could flip on one row; three cannot, and a
                        row whose label is the OTHER state ("Classic view" while you are in Dynamic) stops
                        being readable the moment there is more than one other state. Each row now names
                        itself and the current one is ticked. */}
                    {!kiosk && workspaceMode.enabled && workspaceMode.loaded && (
                      <>
                        <div style={menuHeading}>Dial view</div>
                        {WORKSPACE_VIEWS.map((view) => (
                          <button
                            key={view.mode}
                            onClick={() => { void workspaceMode.setMode(view.mode); }}
                            className="kd-row-hover"
                            style={menuItem}
                            disabled={workspaceMode.busy}
                            title={view.hint}
                          >
                            <Icon name={view.icon} size={16} style={{ color: "var(--text-faint)" }} />
                            {view.label}
                            {workspaceMode.mode === view.mode && (
                              <Icon name="check" size={15} style={{ marginLeft: "auto", color: "var(--accent-text)" }} />
                            )}
                          </button>
                        ))}
                        {workspaceMode.refused && (
                          <div role="alert" style={{ ...menuHeading, textTransform: "none", letterSpacing: 0, color: "var(--st-overdue)", fontWeight: 600 }}>
                            That view couldn&apos;t be saved. Reload and try again.
                          </div>
                        )}
                        <div style={menuDivider} />
                      </>
                    )}
                    {/* KEY-543: back in the menu. It moved to the Settings page when this menu was tidied,
                        but a theme is changed in passing — because the room changed, or the glare did —
                        and a two-click trip through a settings page is the wrong shape for that.
                        KEY-755: a list read from the theme registry, not a light/dark flip — the same reasoning
                        as the Dial view list above: a row naming the OTHER state stops working past two. It
                        lives inside this menu, so it costs the header's width budget nothing. */}
                    {!kiosk && (
                      <>
                        <div style={menuHeading}>Theme</div>
                        {themes.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => setTheme(option.id)}
                            className="kd-row-hover"
                            style={menuItem}
                            title={option.description}
                            aria-pressed={theme === option.id}
                          >
                            <Icon name={option.icon} size={16} style={{ color: "var(--text-faint)" }} />
                            {option.label}
                            {theme === option.id && (
                              <Icon name="check" size={15} style={{ marginLeft: "auto", color: "var(--accent-text)" }} />
                            )}
                          </button>
                        ))}
                        <div style={menuDivider} />
                      </>
                    )}
                    {/* KEY-817: fetch my calendars now rather than at the next 15-minute sweep. Only shown to someone
                        with a calendar connected; not on a shared tablet, where the person signed in may not be here. */}
                    {!kiosk && <SyncCalendarsMenuItem style={menuItem} />}
                    {!kiosk && (
                      <button onClick={() => { setMenu(false); navigate("/settings"); }} className="kd-row-hover" style={menuItem} data-help-anchor={HELP_ANCHORS.accountSettingsLink}>
                        <Icon name="gear" size={16} style={{ color: "var(--text-faint)" }} />
                        Settings
                      </button>
                    )}
                    <button onClick={() => { setMenu(false); void logout(); }} className="kd-row-hover" style={menuItem}>
                      <Icon name="logout" size={16} style={{ color: "var(--text-faint)" }} />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {modal === "help" && <HelpLauncher onClose={() => setModal(null)} onOpenVideos={() => setModal("videos")} />}
      {modal === "videos" && <HelpModal onClose={() => setModal(null)} />}
      {me && !kiosk && <WhatsNewLauncher onState={handleWhatsNewState} />}
    </>
  );
}
